import { getOutgoingEdges, findNode } from './graphUtils';

/**
 * Get possible starting nodes for the walker (typically diagnosis nodes).
 */
export function getStartNodes(nodes) {
  return nodes.filter((n) => n.type === 'diagnosis');
}

/**
 * Given a node and all edges/nodes, return the available next steps.
 * For decision nodes, returns structured options matching the node's options array.
 * For other nodes, returns outgoing edges as options.
 *
 * Every declared option is always returned. An option the data cannot route is
 * flagged `unavailable` so the UI can show it greyed out — dropping it silently
 * would make the walker disagree with the flowchart about what the guideline offers.
 */
export function getNextOptions(nodeId, allNodes, allEdges) {
  const node = findNode(allNodes, nodeId);
  if (!node) return [];

  const outgoing = getOutgoingEdges(allEdges, nodeId);

  if (node.type === 'decision' && node.options) {
    // An option may have several edges — one answer can lead to a choice of
    // regimens. Each becomes its own selectable step rather than only the first.
    const byCondition = new Map();
    outgoing.forEach((e) => {
      if (typeof e.conditionIndex !== 'number') return;
      if (!byCondition.has(e.conditionIndex)) byCondition.set(e.conditionIndex, []);
      byCondition.get(e.conditionIndex).push(e);
    });

    // A lone edge carrying no conditionIndex means every branch converges on the
    // same next step, so it serves as the default for otherwise unmapped options.
    const unconditioned = outgoing.filter((e) => typeof e.conditionIndex !== 'number');
    const convergent = unconditioned.length === 1 ? unconditioned[0] : null;

    const result = [];
    node.options.forEach((opt, idx) => {
      const matches = byCondition.get(idx) || (convergent ? [convergent] : []);
      if (matches.length === 0) {
        result.push({
          key: `opt-${idx}`,
          label: opt.label,
          description: opt.description,
          targetNodeId: null,
          edgeLabel: null,
          optionIndex: idx,
          unavailable: true,
        });
        return;
      }
      matches.forEach((edge, j) => {
        const targetNode = findNode(allNodes, edge.target);
        const multi = matches.length > 1;
        result.push({
          key: `opt-${idx}-${j}`,
          label: multi ? edge.label || targetNode?.label || opt.label : opt.label,
          description: multi
            ? [opt.label, targetNode?.label].filter(Boolean).join(' → ')
            : opt.description,
          targetNodeId: edge.target,
          edgeLabel: edge.label,
          optionIndex: idx,
          unavailable: false,
        });
      });
    });
    return result;
  }

  // Non-decision nodes: auto-advance if single outgoing, show choices if multiple
  return outgoing.map((e, idx) => {
    const targetNode = findNode(allNodes, e.target);
    return {
      key: `edge-${e.id || idx}`,
      label: e.label || targetNode?.label || 'Continue',
      description: targetNode?.detail || '',
      targetNodeId: e.target,
      edgeLabel: e.label,
      optionIndex: idx,
      unavailable: false,
    };
  });
}

/**
 * Options the user can actually act on.
 */
export function selectableOptions(options) {
  return options.filter((o) => !o.unavailable);
}

/**
 * Check if a node is terminal (no outgoing edges).
 */
export function isTerminalNode(nodeId, allEdges) {
  return getOutgoingEdges(allEdges, nodeId).length === 0;
}

/**
 * A step is the end of the road when nothing can be selected from it — either
 * because it has no outgoing edges, or because none of its options route anywhere.
 */
export function isEndOfPath(nodeId, allNodes, allEdges) {
  if (isTerminalNode(nodeId, allEdges)) return true;
  return selectableOptions(getNextOptions(nodeId, allNodes, allEdges)).length === 0;
}

/**
 * Build a summary of the walker's path from history.
 */
export function buildPathSummary(history, currentNodeId, allNodes) {
  const steps = history.map((entry) => {
    const node = findNode(allNodes, entry.nodeId);
    return {
      nodeId: entry.nodeId,
      label: node?.label || entry.nodeId,
      type: node?.type || 'unknown',
      selectedOption: entry.selectedOptionIndex,
    };
  });

  const currentNode = findNode(allNodes, currentNodeId);
  if (currentNode) {
    steps.push({
      nodeId: currentNodeId,
      label: currentNode.label,
      type: currentNode.type,
      selectedOption: null,
    });
  }

  return steps;
}

/**
 * Check if a reference node should trigger a page change.
 */
export function isPageReference(node) {
  return node?.type === 'reference' && node?.targetPage;
}
